import { useState, useEffect } from 'react';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription, UpgradePromptDialog } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Mail, Trash2, Clock, CheckCircle2, 
  AlertCircle, Crown, Lock, Loader2
} from 'lucide-react';

export const TeamPage = () => {
  const { user, token } = useAuth();
  const { isReadOnly, isPro, isRider, isTrialActive } = useSubscription();
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [removeMemberDialog, setRemoveMemberDialog] = useState({ open: false, member: null });
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchTeamData();
  }, [token]);

  const fetchTeamData = async () => {
    if (!token) return;
    const headers = getHeaders();
    
    try {
      // Fetch team info (includes members for owners)
      const teamResponse = await fetch(`${API}/team`, { headers });
      if (teamResponse.ok) {
        const teamData = await teamResponse.json();
        setTeamInfo(teamData);
        
        // For owners, members are included in the response
        if (teamData.members) {
          setTeamMembers(teamData.members.filter(m => m.status === 'active'));
          setPendingInvites(teamData.members.filter(m => m.status === 'pending'));
        }
      }
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    if (!inviteName.trim()) {
      toast.error('Please enter their name');
      return;
    }

    setIsInviting(true);
    try {
      const response = await fetch(`${API}/team/invite`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ 
          email: inviteEmail.trim().toLowerCase(),
          name: inviteName.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Invitation sent to ${inviteName}`);
        setInviteEmail('');
        setInviteName('');
        fetchTeamData();
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    try {
      const response = await fetch(`${API}/team/${memberId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success(`${memberEmail} has been removed from your team`);
        setRemoveMemberDialog({ open: false, member: null });
        fetchTeamData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove team member');
      }
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  const confirmRemoveMember = (member) => {
    setRemoveMemberDialog({ open: true, member });
  };

  const handleCancelInvite = async (memberId, memberEmail) => {
    try {
      const response = await fetch(`${API}/team/${memberId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success(`Invitation to ${memberEmail} cancelled`);
        fetchTeamData();
      } else {
        toast.error('Failed to cancel invitation');
      }
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  // Check if user is a team member (not owner)
  const isTeamMember = teamInfo && !teamInfo.is_owner && teamInfo.team_owner_id;

  // Determine team limits based on plan
  const getTeamLimit = () => {
    if (isPro()) return -1; // Unlimited
    if (isRider()) return 1;
    if (isTrialActive) return 2; // Trial gets to test with 2
    return 0;
  };

  const teamLimit = getTeamLimit();
  const currentTeamSize = teamMembers.length + pendingInvites.length;
  const canInviteMore = teamLimit === -1 || currentTeamSize < teamLimit;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-forest" />
        </div>
      </Layout>
    );
  }

  // If user is a team member, show different view
  if (isTeamMember) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Team Access</h1>
            <p className="text-muted-foreground mt-1">You're a team member on another account</p>
          </div>

          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-forest" />
                </div>
                <div>
                  <p className="font-medium">You have access to:</p>
                  <p className="text-lg font-semibold text-forest">{teamInfo.team_owner_name}'s Stable</p>
                  <p className="text-sm text-muted-foreground">
                    Use the Account Switcher in the sidebar to switch between your personal account and this team.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Team Management</h1>
          <p className="text-muted-foreground mt-1">
            Invite helpers to manage your horses
          </p>
        </div>

        {/* Plan Info */}
        <Card className="border-0 shadow-card bg-gradient-to-r from-forest/5 to-forest/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-forest" />
                <div>
                  <p className="font-medium">
                    {isPro() ? 'Pro Plan' : isRider() ? 'Rider Plan' : isTrialActive ? 'Trial' : 'Free'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {teamLimit === -1 
                      ? 'Unlimited team members' 
                      : `${teamLimit} team member${teamLimit !== 1 ? 's' : ''} allowed`}
                  </p>
                </div>
              </div>
              <Badge variant={canInviteMore ? 'default' : 'secondary'} className="bg-forest">
                {currentTeamSize} / {teamLimit === -1 ? '∞' : teamLimit} used
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Invite Form */}
        <Card className="border-0 shadow-card bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserPlus className="w-5 h-5 text-forest" />
              Invite Team Member
            </CardTitle>
            <CardDescription>
              Enter their email address. They'll receive an invitation to join your stable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isReadOnly ? (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                <Lock className="w-4 h-4" />
                <span className="text-sm">Subscribe to invite team members</span>
              </div>
            ) : !canInviteMore ? (
              <div className="flex items-center justify-between gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">
                    You've reached your team limit ({teamLimit}).
                  </span>
                </div>
                {!isPro() && (
                  <Button 
                    size="sm" 
                    className="bg-forest hover:bg-forest-dark text-white"
                    onClick={() => setShowUpgradeDialog(true)}
                  >
                    Upgrade to Pro
                  </Button>
                )}
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-3">
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Name (e.g. Sarah)"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    className="flex-1"
                    data-testid="team-invite-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1"
                    data-testid="team-invite-email"
                  />
                  <Button 
                    type="submit" 
                    className="bg-forest hover:bg-forest-dark gap-2"
                    disabled={isInviting}
                    data-testid="team-invite-btn"
                  >
                    {isInviting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4" /> Send Invite</>
                    )}
                  </Button>
                </div>
              </form>
            )}
            
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>What happens next?</strong> They'll receive an email invitation. 
                Once they sign up or log in with that email, they'll automatically have access to view and manage your horses.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <Card className="border-0 shadow-card bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Invitations
                <Badge variant="secondary">{pendingInvites.length}</Badge>
              </CardTitle>
              <CardDescription>
                Waiting for these people to accept your invitation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingInvites.map((invite) => (
                  <div 
                    key={invite.member_id} 
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">{invite.name || 'Unnamed'}</p>
                        <p className="text-sm text-muted-foreground">{invite.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Invited {new Date(invite.invited_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvite(invite.member_id, invite.email)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Team Members */}
        <Card className="border-0 shadow-card bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-forest" />
              Team Members
              {teamMembers.length > 0 && <Badge className="bg-forest">{teamMembers.length}</Badge>}
            </CardTitle>
            <CardDescription>
              People who can help manage your horses
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No team members yet</p>
                <p className="text-sm">Invite someone above to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamMembers.map((member) => (
                  <div 
                    key={member.member_id} 
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-forest" />
                      </div>
                      <div>
                        <p className="font-medium">{member.name || member.email}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => confirmRemoveMember(member)}
                      className="text-muted-foreground hover:text-destructive hover:border-destructive"
                      data-testid={`remove-member-${member.member_id}`}
                    >
                      Remove Access
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="border-0 shadow-card bg-muted/30">
          <CardContent className="p-4">
            <h3 className="font-medium mb-2">How team access works</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Team members can view and edit all your horses, records, and expenses</li>
              <li>• They cannot manage your subscription or invite other team members</li>
              <li>• Team members can also have their own personal horses on their account</li>
              <li>• Use the Account Switcher in the sidebar to switch between accounts</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog 
        open={removeMemberDialog.open} 
        onOpenChange={(open) => !open && setRemoveMemberDialog({ open: false, member: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{removeMemberDialog.member?.name || removeMemberDialog.member?.email}</strong> from your team?
              <br /><br />
              They will lose access to your horses but their personal account will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRemoveMember(removeMemberDialog.member?.member_id, removeMemberDialog.member?.email)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Upgrade prompt dialog */}
      <UpgradePromptDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        type="team"
        currentCount={currentTeamSize}
        limit={teamLimit}
      />
    </Layout>
  );
};

export default TeamPage;
